import { useEffect, useState } from "react";
import useSWR from "swr";
import { KeyRound, Trash2, UserPlus, UsersRound } from "lucide-react";
import { ApiError, apiFetch, swrFetcher, type InvitationCredentials, type TeamMember } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";

type Role = "team_admin" | "producer" | "viewer";

export function TeamMembersPanel({ teamId, canManage, canIssueCredentials = false, onCredentials }: { teamId: string; canManage: boolean; canIssueCredentials?: boolean; onCredentials: (value: InvitationCredentials) => void }) {
  const { data: members, isLoading, mutate } = useSWR<TeamMember[]>(`/api/teams/${teamId}/members`, swrFetcher);
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState<Role>("producer");
  const [saving, setSaving] = useState(false);
  const [renewing, setRenewing] = useState<string | null>(null);
  const [confirmingCredentials, setConfirmingCredentials] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  async function invite() {
    if (!email.trim()) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const result = await apiFetch<{ created_user: boolean; invite_url: string | null; temporary_password: string | null; expires_at: string | null }>(`/api/teams/${teamId}/invites`, { method: "POST", body: JSON.stringify({ email: email.trim(), full_name: fullName.trim() || null, role }) });
      setEmail("");
      setFullName("");
      setRole("producer");
      await mutate();
      if (result.invite_url && result.temporary_password && result.expires_at) onCredentials({ invite_url: result.invite_url, temporary_password: result.temporary_password, expires_at: result.expires_at });
      else setMessage("Existing user added to this team.");
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Member could not be invited.");
    } finally {
      setSaving(false);
    }
  }

  async function changeRole(userId: string, nextRole: Role) {
    setError(null);
    try {
      await apiFetch(`/api/teams/${teamId}/members/${userId}`, { method: "PATCH", body: JSON.stringify({ role: nextRole }) });
      await mutate();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Role could not be changed.");
    }
  }

  async function remove(userId: string) {
    setError(null);
    try {
      await apiFetch(`/api/teams/${teamId}/members/${userId}`, { method: "DELETE" });
      setRemoving(null);
      await mutate();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Member could not be removed.");
    }
  }

  async function issueCredentials(userId: string) {
    setRenewing(userId);
    setError(null);
    setMessage(null);
    try {
      const result = await apiFetch<InvitationCredentials>(`/api/admin/users/${userId}/invite`, { method: "POST" });
      setConfirmingCredentials(null);
      onCredentials(result);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Temporary credentials could not be generated.");
    } finally {
      setRenewing(null);
    }
  }

  return (
    <div className="space-y-4">
      {canManage && (
        <div className="rounded-2xl border border-studio-border bg-studio-card p-3 sm:p-4">
          <div className="mb-3 flex items-center gap-2"><UserPlus className="h-4 w-4 text-studio-accent" /><h3 className="text-sm font-semibold text-studio-ink">Invite member</h3></div>
          <div className="grid gap-2 sm:grid-cols-2"><label className="text-xs font-medium text-studio-muted">Email<Input className="mt-1.5" type="email" value={email} placeholder="person@example.com" onChange={(event) => setEmail(event.target.value)} /></label><label className="text-xs font-medium text-studio-muted">Full name<Input className="mt-1.5" value={fullName} placeholder="Optional" onChange={(event) => setFullName(event.target.value)} /></label></div>
          <div className="mt-2 flex flex-col gap-2 sm:flex-row"><label className="min-w-0 flex-1 text-xs font-medium text-studio-muted">Team role<Select className="mt-1.5" value={role} onChange={(event) => setRole(event.target.value as Role)}><option value="producer">Producer</option><option value="viewer">Viewer</option><option value="team_admin">Team admin</option></Select></label><Button className="mt-auto active:scale-[0.96] transition-transform" onClick={() => void invite()} loading={saving} disabled={!email.trim()}>Invite</Button></div>
        </div>
      )}

      {error && <p role="alert" className="rounded-lg bg-studio-danger/10 px-3 py-2 text-xs text-studio-danger">{error}</p>}
      {message && <p role="status" className="rounded-lg bg-studio-success/10 px-3 py-2 text-xs text-studio-success">{message}</p>}
      <div className="overflow-hidden rounded-2xl border border-studio-border">
        <div className="flex items-center gap-2 border-b border-studio-border bg-studio-card px-3 py-2.5"><UsersRound className="h-4 w-4 text-studio-muted" /><span className="text-xs font-semibold text-studio-muted">{members?.length ?? 0} members</span></div>
        {isLoading ? <p className="p-4 text-sm text-studio-muted">Loading members…</p> : members?.length ? members.map((member) => (
          <div key={member.user_id} className="flex flex-col gap-3 border-b border-studio-border px-3 py-3 last:border-b-0 sm:flex-row sm:flex-wrap sm:items-center">
            <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium text-studio-ink">{member.full_name || member.email}</p><p className="truncate text-xs text-studio-faint">{member.email}</p></div>
            {canManage ? <Select className="sm:w-36" value={member.role} onChange={(event) => void changeRole(member.user_id, event.target.value as Role)}><option value="producer">Producer</option><option value="viewer">Viewer</option><option value="team_admin">Team admin</option></Select> : <span className="text-xs capitalize text-studio-muted">{member.role.replace(/_/g, " ")}</span>}
            {canManage && (
              <div className="flex items-center gap-1">
                {canIssueCredentials && (
                  confirmingCredentials === member.user_id ? (
                    <div className="flex gap-1">
                      <Button size="sm" variant="secondary" loading={renewing === member.user_id} onClick={() => void issueCredentials(member.user_id)}>Generate</Button>
                      <Button size="sm" variant="ghost" disabled={renewing === member.user_id} onClick={() => setConfirmingCredentials(null)}>Cancel</Button>
                    </div>
                  ) : (
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label={`Generate a temporary password for ${member.email}`}
                      title="Generate temporary password"
                      onClick={() => {
                        setRemoving(null);
                        setConfirmingCredentials(member.user_id);
                      }}
                    >
                      <KeyRound className="h-4 w-4 text-studio-accent" />
                    </Button>
                  )
                )}
                {removing === member.user_id ? (
                  <div className="flex gap-1">
                    <Button size="sm" variant="danger" onClick={() => void remove(member.user_id)}>Confirm</Button>
                    <Button size="sm" variant="ghost" onClick={() => setRemoving(null)}>Cancel</Button>
                  </div>
                ) : (
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label={`Remove ${member.email}`}
                    onClick={() => {
                      setConfirmingCredentials(null);
                      setRemoving(member.user_id);
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-studio-danger" />
                  </Button>
                )}
              </div>
            )}
            {canManage && (
              <MemberLimitControls
                teamId={teamId}
                member={member}
                adminScope={canIssueCredentials}
                onSaved={async () => { await mutate(); }}
              />
            )}
          </div>
        )) : <p className="p-4 text-sm text-studio-muted">No members yet.</p>}
      </div>
    </div>
  );
}

function MemberLimitControls({ teamId, member, adminScope, onSaved }: { teamId: string; member: TeamMember; adminScope: boolean; onSaved: () => Promise<void> }) {
  const [monthly, setMonthly] = useState(member.monthly_limit_usd ?? "");
  const [perTransaction, setPerTransaction] = useState(member.per_transaction_limit_usd ?? "");
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ tone: "error" | "success"; text: string } | null>(null);
  useEffect(() => {
    setMonthly(member.monthly_limit_usd ?? "");
    setPerTransaction(member.per_transaction_limit_usd ?? "");
  }, [member.monthly_limit_usd, member.per_transaction_limit_usd]);

  async function save() {
    setSaving(true);
    setStatus(null);
    try {
      const path = adminScope
        ? `/api/admin/wallet/${teamId}/members/${member.user_id}/limits`
        : `/api/wallet/members/${member.user_id}/limits`;
      await apiFetch(path, {
        method: "PUT",
        body: JSON.stringify({
          monthly_limit_usd: monthly === "" ? null : Number(monthly),
          per_transaction_limit_usd: perTransaction === "" ? null : Number(perTransaction),
        }),
      });
      await onSaved();
      setStatus({ tone: "success", text: "Spend limits saved." });
    } catch (cause) {
      setStatus({ tone: "error", text: cause instanceof ApiError ? cause.message : "USD spend limits could not be saved." });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="grid w-full grid-cols-2 gap-2 rounded-xl bg-studio-card px-2.5 py-2 sm:grid-cols-[1fr_1fr_auto]">
      <label className="text-[10px] font-medium text-studio-muted">Monthly USD<Input className="mt-1 h-9" type="number" min="0" step="0.01" placeholder="Unlimited" value={monthly} onChange={(event) => setMonthly(event.target.value)} /></label>
      <label className="text-[10px] font-medium text-studio-muted">Per transaction USD<Input className="mt-1 h-9" type="number" min="0" step="0.01" placeholder="Unlimited" value={perTransaction} onChange={(event) => setPerTransaction(event.target.value)} /></label>
      <Button className="col-span-2 mt-auto sm:col-span-1" size="sm" variant="secondary" loading={saving} onClick={() => void save()}>Save limits</Button>
      {status && <p role={status.tone === "error" ? "alert" : "status"} className={`col-span-2 text-[11px] sm:col-span-3 ${status.tone === "error" ? "text-studio-danger" : "text-studio-success"}`}>{status.text}</p>}
    </div>
  );
}
